import React, {useContext, useEffect, useMemo, useState} from "react";
import {useNavigate, useSearchParams} from "react-router-dom";
import {
  Box, Button, ButtonBase, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControl,
  FormControlLabel, FormHelperText, Grid, InputLabel, MenuItem, Select, Stack, Switch, TextField, Typography
} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {
  Add01Icon, ArrowLeft02Icon, ArrowRight01Icon, CallIcon, Delete02Icon, FloppyDiskIcon, InformationCircleIcon,
  LanguageCircleIcon, ServerStack01Icon
} from "@hugeicons/core-free-icons";
import {toast} from "react-toastify";
import {useCatalog} from "../../../context/CatalogContext";
import {Context as AppContext} from "../../../context/AppContext";
import {ProjectEntity} from "../../../model/ModelData";
import useConfirm from "../../../hooks/useConfirm";
import {SubProjectRepository} from "../../../repositories/ProjectRepository";
import {filterByOrganization} from "../../../utils/organizationAccess";
import {subProjectLabel} from "../../../utils/subProjects";
import {
  LanguageConfigurationFields, SubHeader, toApiConfiguration, toVoiceGemini
} from "../../project-creation/components/LanguageConfigurations";
import {fieldSx as baseFieldSx, SectionHeader, selectLabelSx as baseSelectLabelSx} from "../../project-creation/components/ProjectForm";
import {useRegistryProject} from "../RegistryProjectContext";
import {getFlow, mirrorToFlows, updateFlow} from "../flowSync";
import {SettingsFooter} from "./SettingsFooter";

// Phones only (below md): flat fields like the other settings tabs, with the label above the field instead of
// floating on its border. Desktop keeps the outlined fields.
const PHONE = "@media (max-width: 899.95px)";
const fieldSx = {
  ...baseFieldSx,
  [PHONE]: {
    "&.MuiOutlinedInput-root, & .MuiOutlinedInput-root": {bgcolor: "#FAFAFD", borderRadius: "12px"},
    "& .MuiOutlinedInput-notchedOutline": {border: "none"},
    "& .MuiOutlinedInput-notchedOutline legend": {display: "none"},
    "& .MuiInputLabel-root": {
      position: "static !important", transform: "none !important", maxWidth: "100% !important", mb: 0.75, px: 0,
      fontSize: "12px", fontWeight: 600, letterSpacing: "0.05em", textTransform: "uppercase", color: "#6B6B80",
    },
    "& .MuiFormHelperText-root": {mx: 0.5, fontSize: "11.5px", color: "#8A8AA0"},
  },
};
const selectLabelSx = {
  ...baseSelectLabelSx,
  [PHONE]: {
    position: "static !important", transform: "none !important", maxWidth: "100% !important", mb: 0.75, px: 0,
    backgroundColor: "transparent", fontSize: "12px", fontWeight: 600, letterSpacing: "0.05em", textTransform: "uppercase", color: "#6B6B80",
  },
};

const STATUSES = [
  {value: "BUILDING_SCENARIOS", label: "Building Scenarios"},
  {value: "IN_PROGRESS", label: "In Progress"},
  {value: "TESTING", label: "Testing"},
  {value: "PRODUCTION", label: "Production"},
];

// Core configuration keys of the API (snake_case, all values are strings)
const CORE_KEYS = [
  "max_calls_by_user", "chatbot_timeout", "redirect_vdn", "outbound_call", "success_tag", "redirect_message"
];

const DESCRIPTION_MAX = 4000;

// Form state of one sub project; voiceGemini is the voice stored in its Firebase flow, projectBackendId the backend
// configuration of the project, which a sub project without its own falls back to
const toSubProjectConfig = (subProject, voiceGemini, projectBackendId) => {
  const voice = subProject.voiceConfiguration || {};
  const transcribe = subProject.transcribeConfiguration || {};
  const core = subProject.coreConfiguration || {};
  return {
    name: subProject.name || "",
    description: subProject.description || "",
    status: subProject.status || "BUILDING_SCENARIOS",
    live: !!subProject.live,
    backendConfigurationId: subProject.backendConfiguration?.id ?? projectBackendId ?? null,
    voiceConfiguration: {
      voiceModelId: voice.voiceModelId ?? null,
      voice: voice.voice || "",
      languageCode: voice.languageCode || subProject.languageCode || "",
      audioEncoding: voice.audioEncoding || "LINEAR16",
      pitch: voice.pitch ?? 0,
      speakingRate: voice.speakingRate ?? 1,
    },
    transcribeConfiguration: {
      transcribeModelId: transcribe.transcribeModelId ?? null,
      languageCode: transcribe.languageCode || subProject.languageCode || "",
    },
    coreConfiguration: Object.fromEntries(CORE_KEYS.map(key => [key, core[key] ?? ""])),
    // the style prompt is stored only in the Firebase flow
    prompt: voiceGemini?.input?.prompt || "",
  };
};

// The flow runtime reads the core configuration from the flow's `configuration`
const toFlowConfiguration = (core) => ({
  "configuration.max_calls_by_user": core.max_calls_by_user ? +core.max_calls_by_user : null,
  "configuration.chatbot_timeout": core.chatbot_timeout,
  "configuration.redirect_vdn": core.redirect_vdn,
  "configuration.outbound_call": core.outbound_call,
  "configuration.success_tag": core.success_tag || null,
  "configuration.redirect_message": core.redirect_message,
});

// models already used by the project stay selectable even when they are not in the user's own list
const withSelected = (models, selected) => [
  ...models,
  ...selected.filter(item => item.id && !models.some(model => model.id === item.id)),
];

// Flow settings a new sub project takes over from the project's existing flow; its own language and voice are set apart
const INHERITED_FLOW_SETTINGS = ["organization", "organization_type", "speechToText", "projectType", "mode", "category"];

// The sub project opened from the list (?subProject=<id>), if it exists in the project
export const findOpenSubProject = (project, searchParams) =>
  project.subProjects.find(item => String(item.id) === searchParams.get("subProject"));

// Public configurations and those of the project's organization; the ones already in use stay selectable, they may
// not be listed
const useBackendConfigurations = (project) => {
  const {items: myBackendConfigurations} = useCatalog("myBackendConfigurations", {refreshOnMount: true});
  return [...new Map([
    ...filterByOrganization(myBackendConfigurations, project.organizationId),
    project.backendConfiguration,
    ...project.subProjects.map(item => item.backendConfiguration),
  ].filter(Boolean).map(item => [item.id, item])).values()];
};

const backendLabel = (backend, project) =>
  `${backend.name}${backend.id === project.backendConfiguration?.id ? " (project's)" : ""}`;

// Sub projects tab: the list of the project's sub projects, plus adding them. Clicking one opens its own page, where
// it is configured and saved on its own.
export const RegistrySubProjectSettings = () => {
  const {project, subProject: currentSubProject, refreshProject} = useRegistryProject();
  const {createProject} = useContext(AppContext);
  const {items: languages} = useCatalog("languages");
  const backendConfigurations = useBackendConfigurations(project);
  const [searchParams, setSearchParams] = useSearchParams();

  // ?add=1 (from the sub project switcher) opens the add dialog
  const addOpen = searchParams.get("add") === "1";
  const setAddOpen = (open) => setSearchParams(prev => {
    const next = new URLSearchParams(prev);
    if (open) next.set("add", "1"); else next.delete("add");
    return next;
  }, {replace: true});

  const openSubProject = findOpenSubProject(project, searchParams);
  const setOpenSubProject = (id) => setSearchParams(prev => {
    const next = new URLSearchParams(prev);
    if (id) next.set("subProject", id); else next.delete("subProject");
    return next;
  });

  // the selected sub project first
  const subProjects = useMemo(() => [...project.subProjects].sort((a, b) =>
    (b.id === currentSubProject?.id) - (a.id === currentSubProject?.id)
  ), [project.subProjects, currentSubProject?.id]);

  // Creates the sub project, then its Firebase flow (the designer and the flow runtime read it). The flow takes the
  // organization, members and type of the project's existing flow.
  const handleAdd = async ({name, description, languageId, backendConfigurationId}) => {
    let created = null;
    try {
      const response = await SubProjectRepository.create({
        projectId: project.id,
        languageId,
        name: name || null,
        description: description || null,
        // null takes the project's; sending that id could be refused for a manager outside its organization
        backendConfigurationId: backendConfigurationId !== project.backendConfiguration?.id ? backendConfigurationId || null : null,
      });
      created = response.data;
      if (created?.flowUid) {
        const template = await getFlow(project.subProjects.find(item => item.flowUid)).catch(() => null);
        const language = languages.find(item => item.id === languageId);
        const flow = {
          ...JSON.parse(JSON.stringify(ProjectEntity)),
          id: created.flowUid,
          name: `${project.name} (${subProjectLabel(created)})`,
          description: created.description || project.description || "",
          is_demo: false,
          createdAt: new Date().getTime(),
          status: created.status || "BUILDING_SCENARIOS",
        };
        INHERITED_FLOW_SETTINGS.forEach(key => {
          if (template?.settings?.[key] !== undefined) flow.settings[key] = template.settings[key];
        });
        flow.settings.organization = project.organizationId || flow.settings.organization || null;
        flow.settings.language = languageId;
        flow.settings.lang = language?.lang || null;
        flow.settings.voiceGemini = null;
        flow.settings.project_id = project.id;
        flow.settings.subproject_id = created.id;
        if (template?.members) flow.members = template.members;
        await createProject(flow);
      }
      await refreshProject();
      toast.success(`Added "${subProjectLabel(created)}"`);
      return true;
    } catch (error) {
      console.error(error);
      if (created) {
        // the sub project exists; only its flow failed, the designer reports a missing flow
        await refreshProject();
        toast.warn("The sub project was added, but its flow could not be created");
        return true;
      }
      toast.error(error?.response?.data?.message || "Failed to add the sub project");
      return false;
    }
  };

  if (openSubProject) {
    return (
      <SubProjectDetail
        key={openSubProject.id}
        item={openSubProject}
        backendConfigurations={backendConfigurations}
        onClose={() => setOpenSubProject(null)}
      />
    );
  }

  return (
    <>
      <AddSubProjectDialog
        open={addOpen}
        handleClose={() => setAddOpen(false)}
        languages={languages || []}
        subProjects={project.subProjects}
        backendConfigurations={backendConfigurations}
        project={project}
        onSubmit={handleAdd}
      />
      <Box sx={{px: {xs: 2, md: 4}, py: {xs: 2, md: 3}}}>
        <Stack direction={{xs: "column", sm: "row"}} justifyContent={"space-between"} alignItems={{sm: "flex-start"}} gap={1}>
          <SectionHeader
            icon={<HugeiconsIcon icon={LanguageCircleIcon} size={18}/>}
            title={"Sub Projects"}
            description={"Each sub project has its own language, flow, backend, voice and call handling."}
          />
          <Button
            type={"button"} variant={"primary"} onClick={() => setAddOpen(true)} sx={{flexShrink: 0, width: {xs: "100%", sm: "auto"}, mb: {xs: 2, sm: 0}}}
            startIcon={<HugeiconsIcon icon={Add01Icon} size={18}/>}
          >
            Add Sub Project
          </Button>
        </Stack>
        <Stack gap={1} sx={{mt: 2}}>
          {subProjects.map(item => (
            <SubProjectRow key={item.id} item={item} project={project} onClick={() => setOpenSubProject(item.id)}/>
          ))}
        </Stack>
      </Box>
    </>
  );
};

// One row of the list: what the sub project is and the configuration it runs on
const SubProjectRow = ({item, project, onClick}) => {
  const backend = item.backendConfiguration || project.backendConfiguration;
  const status = STATUSES.find(option => option.value === item.status)?.label || item.status;
  const details = [
    status,
    `Backend: ${backend ? backendLabel(backend, project) : "Not configured"}`,
    `Voice: ${item.voiceConfiguration?.voiceModelName || "Default"}`,
    `Transcribe: ${item.transcribeConfiguration?.transcribeModelName || "Default"}`,
  ].filter(Boolean).join(" · ");

  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        display: "flex", justifyContent: "space-between", gap: 1.5, width: "100%", p: 1.75, textAlign: "left",
        borderRadius: "12px", border: "1px solid #ECECF3", "&:hover": {bgcolor: "#FAFAFD", borderColor: "#D9DBF5"},
      }}
    >
      <Box sx={{minWidth: 0}}>
        <Stack direction={"row"} alignItems={"center"} gap={1}>
          <Box
            component={"span"} title={item.live ? "Live" : "Not live"}
            sx={{width: 8, height: 8, borderRadius: "50%", flexShrink: 0, bgcolor: item.live ? "#22C55E" : "#C9CAD8"}}
          />
          <Typography noWrap sx={{fontSize: 14, fontWeight: 700, color: "#23264D"}}>{subProjectLabel(item)}</Typography>
          {item.languageCode && <Chip label={item.languageCode} size={"small"} title={item.languageName}/>}
        </Stack>
        <Typography sx={{fontSize: 12.5, color: "#8A8AA0", mt: 0.5}}>{details}</Typography>
      </Box>
      <Box sx={{display: "flex", flexShrink: 0, color: "#8A8AA0"}}><HugeiconsIcon icon={ArrowRight01Icon} size={18}/></Box>
    </ButtonBase>
  );
};

// Page of one sub project: name, description, status, availability, backend, voice, transcribe and core configuration,
// saved on their own. onClose goes back to the list.
const SubProjectDetail = ({item, backendConfigurations, onClose}) => {
  const {project, subProject: currentSubProject, refreshProject} = useRegistryProject();
  const {items: myVoiceModels} = useCatalog("myVoiceModels", {refreshOnMount: true});
  const {items: myTranscribeModels} = useCatalog("myTranscribeModels", {refreshOnMount: true});
  const navigate = useNavigate();
  const confirm = useConfirm();
  // {voiceGemini, tags} of the sub project's flow, null until loaded
  const [flow, setFlow] = useState(null);
  const [config, setConfig] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // keyed by the flow id so a project refresh after saving does not reload the flow
  useEffect(() => {
    let cancelled = false;
    setFlow(null);
    (item.flowUid ? getFlow(item) : Promise.resolve(null))
      .then(data => !cancelled && setFlow({voiceGemini: data?.settings?.voiceGemini || null, tags: data?.settings?.tags || []}))
      .catch(e => {
        console.error(e);
        if (!cancelled) toast.error("Failed to load the sub project's flow");
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.flowUid]);

  const savedConfig = useMemo(
    () => flow && toSubProjectConfig(item, flow.voiceGemini, project.backendConfiguration?.id),
    [item, project.backendConfiguration?.id, flow]
  );

  useEffect(() => {
    if (savedConfig) setConfig(savedConfig);
  }, [savedConfig]);

  // only public models and those of the project's organization can be used
  const voiceModels = withSelected(
    filterByOrganization(myVoiceModels, project.organizationId),
    [{id: item.voiceConfiguration?.voiceModelId, name: item.voiceConfiguration?.voiceModelName}]
  );
  const transcribeModels = withSelected(
    filterByOrganization(myTranscribeModels, project.organizationId),
    [{
      id: item.transcribeConfiguration?.transcribeModelId,
      name: item.transcribeConfiguration?.transcribeModelName,
      languageId: item.languageId,
    }]
  );

  const hasChanges = Boolean(savedConfig && config) && JSON.stringify(config) !== JSON.stringify(savedConfig);

  // names are unique within the project (case-insensitive); a blank name keeps the current one
  const name = (config?.name || "").trim().toLowerCase();
  const nameError = name && project.subProjects.some(other => other.id !== item.id && (other.name || "").trim().toLowerCase() === name)
    ? "Another sub project already has this name"
    : null;

  const toFlowVoice = () => {
    const voiceGemini = toVoiceGemini(config, voiceModels);
    // a model outside the user's own list has no known model id, keep the one the flow already has
    if (voiceGemini && !voiceGemini.voice.modelName
      && config.voiceConfiguration.voiceModelId === item.voiceConfiguration?.voiceModelId) {
      voiceGemini.voice.modelName = flow?.voiceGemini?.voice?.modelName || null;
    }
    return voiceGemini;
  };

  const handleBack = async () => {
    if (hasChanges && !(await confirm({
      title: "Discard unsaved changes?",
      message: `The changes to "${subProjectLabel(item)}" have not been saved.`,
      confirmText: "Discard",
    }))) return;
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (nameError) {
      toast.error("Sub project names must be unique");
      return;
    }
    setSaving(true);
    try {
      const {description, status, live, coreConfiguration, backendConfigurationId} = config;
      await SubProjectRepository.edit(item.id, {
        ...toApiConfiguration(config),
        // only when changed: an unchanged id is refused for a manager outside the configuration's organization
        ...(backendConfigurationId !== savedConfig.backendConfigurationId ? {backendConfigurationId} : {}),
        // null keeps the current value: a cleared name keeps the old one, a cleared description is saved empty
        name: config.name.trim() || null,
        description: description.trim(),
        status, live, coreConfiguration,
      });
      const voiceGemini = toFlowVoice();
      await mirrorToFlows([item], subProject => updateFlow(subProject, {
        "settings.voiceGemini": voiceGemini,
        status,
        "settings.status": status,
        "configuration.is_live": live,
        ...toFlowConfiguration(coreConfiguration),
      }));
      await refreshProject();
      setFlow(prev => ({...prev, voiceGemini}));
      toast.success(`Saved "${subProjectLabel({...item, name: config.name.trim() || item.name})}"`);
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.message || "Failed to update the sub project");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    const confirmed = await confirm({
      title: `Delete "${subProjectLabel(item)}"?`,
      message: "Its configuration and versions are deleted. This cannot be undone.",
      confirmText: "Delete",
    });
    if (!confirmed) return;
    setDeleting(true);
    try {
      await SubProjectRepository.delete(item.id);
      // the page of a deleted sub project no longer exists, move to another one first
      if (item.id === currentSubProject?.id) {
        const next = project.subProjects.find(other => other.id !== item.id);
        navigate(`/registry-project/${project.id}/${next.id}/settings?tab=sub-projects`, {replace: true});
      } else {
        onClose();
      }
      await refreshProject();
      toast.success(`Deleted "${subProjectLabel(item)}"`);
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.message || "Failed to delete the sub project");
      setDeleting(false);
    }
  };

  const onlySubProject = project.subProjects.length <= 1;

  return (
    <Box component={"form"} onSubmit={handleSubmit}>
      <Box sx={{px: {xs: 2, md: 4}, py: {xs: 2, md: 3}}}>
        <Button
          type={"button"} variant={"ghost"} color={"secondary"} onClick={handleBack} disabled={saving || deleting}
          startIcon={<HugeiconsIcon icon={ArrowLeft02Icon} size={16}/>} sx={{mb: 1.5, ml: -1}}
        >
          Sub Projects
        </Button>
        <SectionHeader
          icon={<HugeiconsIcon icon={LanguageCircleIcon} size={18}/>}
          title={subProjectLabel(item)}
          description={`Configuration of this sub project in ${project.name}.`}
        />
        {!config ? (
          <Stack alignItems={"center"} sx={{py: 4}}><CircularProgress size={28}/></Stack>
        ) : (
          <Stack gap={3} sx={{mt: 2}}>
            <Box>
              <SubHeader icon={InformationCircleIcon} title={"General"}/>
              <Grid container spacing={2} alignItems={"center"}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label={"Name"} fullWidth value={config.name} sx={fieldSx} InputLabelProps={{shrink: true}}
                    onChange={e => setConfig({...config, name: e.target.value})}
                    placeholder={item.languageName} inputProps={{maxLength: 255}}
                    error={Boolean(nameError)} helperText={nameError || " "}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label={"Language"} fullWidth disabled value={item.languageName || item.languageCode || ""}
                    sx={fieldSx} InputLabelProps={{shrink: true}} helperText={"The language cannot be changed"}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label={"Description"} fullWidth multiline minRows={2} value={config.description} sx={fieldSx}
                    InputLabelProps={{shrink: true}} inputProps={{maxLength: DESCRIPTION_MAX}}
                    onChange={e => setConfig({...config, description: e.target.value})}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel id={`status-${item.id}`} shrink sx={selectLabelSx}>Status</InputLabel>
                    <Select
                      labelId={`status-${item.id}`}
                      label={"Status"}
                      value={config.status}
                      sx={fieldSx}
                      onChange={e => setConfig({...config, status: e.target.value})}
                    >
                      {STATUSES.map(status => <MenuItem key={status.value} value={status.value}>{status.label}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    label={config.live ? "Live" : "Not live"}
                    control={
                      <Switch
                        checked={config.live}
                        color={"secondary"}
                        onChange={(e, live) => setConfig({...config, live})}
                      />
                    }
                  />
                </Grid>
              </Grid>
            </Box>

            <Box>
              <SubHeader icon={ServerStack01Icon} title={"Backend Configuration"}/>
              <FormControl fullWidth>
                <InputLabel id={`backend-${item.id}`} shrink sx={selectLabelSx}>Backend Configuration</InputLabel>
                <Select
                  labelId={`backend-${item.id}`}
                  label={"Backend Configuration"}
                  value={config.backendConfigurationId ?? ""}
                  displayEmpty
                  sx={fieldSx}
                  onChange={e => setConfig({...config, backendConfigurationId: e.target.value})}
                >
                  {/* it cannot be cleared: "Not configured" is only there while nothing is set */}
                  {config.backendConfigurationId == null && <MenuItem value={""} disabled><em>Not configured</em></MenuItem>}
                  {backendConfigurations.map(backend => (
                    <MenuItem key={backend.id} value={backend.id}>{backendLabel(backend, project)}</MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  {config.backendConfigurationId === project.backendConfiguration?.id
                    ? "Uses the project's configuration and follows it when the project's changes"
                    : "Differs from the project's configuration. Browser calls still register on the project's PBX."}
                </FormHelperText>
              </FormControl>
            </Box>

            <Box>
              <LanguageConfigurationFields
                languageId={item.id}
                language={{id: item.id, languageId: item.languageId, language: subProjectLabel(item), code: item.languageCode}}
                config={config}
                voiceModels={voiceModels}
                transcribeModels={transcribeModels}
                fieldSx={fieldSx}
                selectLabelSx={selectLabelSx}
                onChange={(id, next) => setConfig(next)}
              />
            </Box>

            <Box>
              <CoreConfigurationFields
                subProjectId={item.id}
                core={config.coreConfiguration}
                tags={flow?.tags || []}
                onChange={(key, value) => setConfig({
                  ...config, coreConfiguration: {...config.coreConfiguration, [key]: value},
                })}
              />
            </Box>

            <Stack
              direction={{xs: "column", sm: "row"}} justifyContent={"space-between"} alignItems={{sm: "center"}} gap={1.5}
              sx={{p: 2, borderRadius: "12px", border: "1px solid #F5C2C0", bgcolor: "#FFF8F7"}}
            >
              <Box>
                <Typography sx={{fontSize: 14, fontWeight: 700, color: "#B42318"}}>Delete Sub Project</Typography>
                <Typography sx={{fontSize: 13, color: "#8A8AA0", mt: 0.25}}>
                  {onlySubProject ? "A project needs at least one sub project." : "Permanently deletes its configuration and versions."}
                </Typography>
              </Box>
              <Button
                type={"button"} variant={"ghost"} color={"error"} disabled={onlySubProject || deleting || saving}
                onClick={handleDelete} sx={{flexShrink: 0}}
                startIcon={deleting
                  ? <CircularProgress size={14} color={"inherit"}/>
                  : <HugeiconsIcon icon={Delete02Icon} size={16}/>}
              >
                Delete
              </Button>
            </Stack>
          </Stack>
        )}
      </Box>

      <SettingsFooter>
        <Button
          type={"button"} variant={"ghost"} color={"secondary"} disabled={!hasChanges || saving}
          onClick={() => setConfig(savedConfig)}
        >
          Reset
        </Button>
        <Button
          type={"submit"} variant={"primary"} disabled={!hasChanges || saving}
          startIcon={saving ? <CircularProgress size={16} color={"inherit"}/> : <HugeiconsIcon icon={FloppyDiskIcon} size={18}/>}
        >
          Save Changes
        </Button>
      </SettingsFooter>
    </Box>
  );
};

// New sub project: a required language, plus a name (defaults to the language's name), a description and a backend
// configuration (defaults to the project's)
const AddSubProjectDialog = ({open, handleClose, languages, subProjects, backendConfigurations, project, onSubmit}) => {
  const projectBackendId = project.backendConfiguration?.id ?? "";
  const [languageId, setLanguageId] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [backendConfigurationId, setBackendConfigurationId] = useState(projectBackendId);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLanguageId("");
    setName("");
    setDescription("");
    setBackendConfigurationId(projectBackendId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const languageName = languages.find(item => item.id === languageId)?.language || "";
  const effectiveName = name.trim() || languageName;
  const duplicate = Boolean(effectiveName) && subProjects.some(item =>
    (item.name || "").trim().toLowerCase() === effectiveName.toLowerCase());

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!languageId || duplicate) return;
    setLoading(true);
    const success = await onSubmit({languageId, name: name.trim(), description: description.trim(), backendConfigurationId});
    setLoading(false);
    if (success) handleClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      PaperProps={{
        component: "form",
        onSubmit: handleSubmit,
        sx: {minWidth: {xs: 0, sm: "360px"}, maxWidth: "sm", borderRadius: 4, p: 2, width: {xs: "calc(100% - 32px)", sm: "100%"}, m: {xs: 2, sm: 4}, boxSizing: "border-box"},
      }}
    >
      <DialogTitle>Add Sub Project</DialogTitle>
      <DialogContent sx={{display: "flex", flexDirection: "column", gap: 2, py: "16px !important"}}>
        <TextField
          select label={"Language"} required fullWidth value={languageId}
          onChange={e => setLanguageId(e.target.value)}
          helperText={"Cannot be changed later"}
        >
          {languages.map(item => <MenuItem key={item.id} value={item.id}>{item.language} ({item.code})</MenuItem>)}
        </TextField>
        <TextField
          label={"Name"} fullWidth value={name} onChange={e => setName(e.target.value)}
          placeholder={languageName || "Defaults to the language name"} InputLabelProps={{shrink: true}}
          inputProps={{maxLength: 255}}
          error={duplicate}
          helperText={duplicate
            ? `"${effectiveName}" is already used in this project${name.trim() ? "" : ", enter a name"}`
            : "Must be unique within the project"}
        />
        <TextField
          label={"Description"} fullWidth multiline minRows={2} value={description}
          onChange={e => setDescription(e.target.value)} inputProps={{maxLength: DESCRIPTION_MAX}}
        />
        <TextField
          select label={"Backend Configuration"} fullWidth value={backendConfigurationId}
          onChange={e => setBackendConfigurationId(e.target.value)} InputLabelProps={{shrink: true}}
          SelectProps={{displayEmpty: true}}
          helperText={backendConfigurationId === projectBackendId
            ? "Uses the project's configuration and follows it when the project's changes"
            : "Differs from the project's configuration. Browser calls still register on the project's PBX."}
        >
          {!projectBackendId && <MenuItem value={""}><em>Not configured</em></MenuItem>}
          {backendConfigurations.map(backend => (
            <MenuItem key={backend.id} value={backend.id}>{backendLabel(backend, project)}</MenuItem>
          ))}
        </TextField>
      </DialogContent>
      <DialogActions sx={{px: 2, py: 1}}>
        <Button variant="ghost" onClick={handleClose} disabled={loading}>Cancel</Button>
        <Button type={"submit"} variant={"modal"} disabled={loading || !languageId || duplicate}>
          {loading ? <CircularProgress size={16} color={"inherit"}/> : "Add Sub Project"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Call handling of one sub project: limits, timeout and where calls are redirected.
// tags are the tags of the sub project's flow, used for the success tag.
const CoreConfigurationFields = ({subProjectId, core, tags, onChange}) => {
  const field = (key) => ({
    value: core[key],
    onChange: e => onChange(key, e.target.value),
    fullWidth: true,
    sx: fieldSx,
    InputLabelProps: {shrink: true},
  });
  // a tag that no longer exists in the flow stays selectable
  const tagOptions = !core.success_tag || tags.some(tag => String(tag?.id) === core.success_tag)
    ? tags
    : [...tags, {id: core.success_tag, name: core.success_tag}];

  return (
    <>
      <SubHeader icon={CallIcon} title={"Core Configuration"}/>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            {...field("max_calls_by_user")}
            label={"Maximum Call Attempts Per User"}
            type={"number"}
            placeholder={"3"}
            inputProps={{min: 0}}
            helperText={"Calls a user can make within 6 hours before being redirected"}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            {...field("chatbot_timeout")}
            label={"Chatbot Timeout"}
            type={"number"}
            placeholder={"27"}
            inputProps={{min: 0}}
            helperText={"Seconds to wait for a response before redirecting"}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            {...field("redirect_vdn")}
            label={"Redirect VDN Number"}
            helperText={"Transfer destination when the chatbot does not respond"}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            {...field("outbound_call")}
            label={"Outbound Caller ID"}
            helperText={"Falls back to the Redirect VDN Number when left empty"}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel id={`success-tag-${subProjectId}`} shrink sx={selectLabelSx}>Success Tag</InputLabel>
            <Select
              labelId={`success-tag-${subProjectId}`}
              label={"Success Tag"}
              value={core.success_tag}
              displayEmpty
              sx={fieldSx}
              onChange={e => onChange("success_tag", e.target.value)}
            >
              <MenuItem value={""}><em>None</em></MenuItem>
              {tagOptions.map(tag => (
                <MenuItem key={tag?.id} value={String(tag?.id)}>{tag?.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <TextField
            {...field("redirect_message")}
            label={"Redirect Message"}
            placeholder={"We're sorry, but the chatbot is currently unavailable to respond. Please hold while we redirect your request."}
            multiline
            rows={3}
          />
        </Grid>
      </Grid>
    </>
  );
};
